import React from 'react';

import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Container,
  Button,
  Flex,
  Heading,
  List,
  ListItem,
  Text,
} from '@brand/components';
import { Image, Seo, Trans } from '@hoa/atoms';

import { useTranslation } from '@hoa/hooks';

import { Layout } from '@hoa/layouts';

import { IconBox } from '@hoa/components/molecules';
import { HomePreFooter } from '@hoa/components/pages/Home/HomePreFooter';
import { GetStaticPaths, GetStaticProps } from 'next';
import { cloudfrontLoader, scrollToElement } from '@hoa/utils';
import { useRef } from 'react';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { css } from '@emotion/react';
import { FaCheckCircle, FaWrench } from 'react-icons/fa';
import { FaHouse } from 'react-icons/fa6';

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [
    // { params: { lang: `en` } },
    { params: { lang: `fr` } },
    { params: { lang: `nl` } },
  ],
  fallback: false,
});

export const getStaticProps: GetStaticProps = context => {
  const lang = context.params?.lang;

  return { props: { lang } };
};

export const Offers = () => {
  const { locales, t } = useTranslation();
  const router = useRouter();

  const expandedIndexRFQ =
    router.query.query === t('common:generic.quote') ? [0] : 99;
  const expandedIndexIR =
    router.query.query === t('common:generic.fixedPrice') ? [0] : 99;

  const IRref = useRef<any>();
  const RFQref = useRef<any>();

  useEffect(() => {
    let timer1 = () =>
      setTimeout(() => {
        scrollToElement(IRref.current, 0);
      }, 1000);
    if (router.query.query === t('common:generic.fixedPrice')) {
      timer1();
    }

    let timer2 = () =>
      setTimeout(() => {
        scrollToElement(RFQref.current, 0);
      }, 1000);
    if (router.query.query === t('common:generic.quote')) {
      timer2();
    }

    return () => {
      clearTimeout(timer1());
      clearTimeout(timer2());
    };
  }, [router.query]);

  const alternativeLinks = locales.map(locale => ({
    locale,
    path: ``,
  }));

  const handleAccordionIROpen = () => {
    scrollToElement(IRref.current, 0);
  };
  const handleAccordionRfqOpen = () => {
    scrollToElement(RFQref.current, 0);
  };

  return (
    <>
      <Seo
        title={t('offers:seo.title')}
        description={t('offers:seo.description')}
        indexed={false}
      />
      <Layout
        alternativeLinks={alternativeLinks}
        hasHeader
        hasFooter={'small'}
        isMenuSticky={true}
      >
        <Box as="section" position="relative">
          <Box
            position="absolute"
            width="100vw"
            h="100%"
            zIndex={-2}
            overflow="hidden"
            css={css`
              clip-path: polygon(0 0, 100% 0, 100% 82%, 0 92%);
            `}
          >
            <Image
              loader={cloudfrontLoader}
              src="/images/hero-images/jaimy_hero_renovation.jpg"
              alt="Jaimy homepage hero image"
              layout="fill"
              objectFit="cover"
              objectPosition="50% 20%"
              quality={60}
            />
          </Box>
          <Container>
            <Flex
              align="center"
              justifyContent="spaceBetween"
              pb={[`65px`, `150px`, `175px`, null]}
              pt={[`40px`, `125px`, `150px`, null]}
            >
              <Box
                bg="rgba(255, 255, 255, 0.8)"
                borderRadius="3xl"
                textAlign="center"
                py={[4, 6, 6, null, 6]}
                px={[4, 6, 10, null, `100px`]}
                m="0 auto"
              >
                <Box>
                  <Heading
                    as="h1"
                    variant={'h2'}
                    color="primary.base"
                    m={2}
                    mb={[6, 8, null, null]}
                  >
                    <Trans i18nKey="common:generic.ourOffers" />
                  </Heading>
                </Box>

                <Text
                  size="lg"
                  as="h2"
                  fontWeight={500}
                  fontStyle={'italic'}
                  mb={[6, 8, null, null]}
                >
                  {t(`home:hero.subtitle`)}
                </Text>
              </Box>
            </Flex>
          </Container>
        </Box>
        <Box py={4}>
          <Container>
            <Box px={[2, 4, 6]} as="section" position="relative">
              <Box pt={[2, null, 16]} pb={[4, null, 8]} bg="white">
                <Flex align="center" justify="center">
                  <Button
                    p={[2, 4, null, null]}
                    onClick={handleAccordionIROpen}
                  >
                    {t(`common:generic.fixedPrice`)}
                  </Button>
                  <Text m={6}>{t(`common:generic.or`)}</Text>
                  <Button
                    p={[2, 4, null, null]}
                    onClick={handleAccordionRfqOpen}
                    variant={`solidBlue`}
                  >
                    {t(`common:generic.quoteSr`)}
                  </Button>
                </Flex>
                <Flex align="center" justify="center" m={[4, null, 6]}>
                  <Heading as="h2" variant="h4" textAlign="center">
                    {t(`offers:generic.subtitle`)}
                  </Heading>
                </Flex>
              </Box>
              <Box
                py={[4, 6, 8]}
                px={[6, null, 8]}
                bg="gray.100"
                borderRadius="10px 10px 0px 0px"
              >
                <Flex align="flex-start" justify="center" direction="column">
                  <Flex align="center" justify="center">
                    <IconBox
                      size="md"
                      icon={<FaCheckCircle color="white" />}
                      background={`heading`}
                      padding="10px"
                      mr={4}
                    />
                    <Box>
                      <Text size={`lg`}>{t(`offers:content.first`)}</Text>
                    </Box>
                  </Flex>
                </Flex>
              </Box>
              <Box py={[4, 6, 8]} px={[6, null, 8]} bg="gray.200">
                <Flex align="flex-start" justify="center" direction="column">
                  <Flex align="center">
                    <IconBox
                      size="md"
                      icon={<FaWrench color="white" />}
                      background={`heading`}
                      padding="10px"
                      mr={4}
                    />
                    <Trans
                      i18nKey="offers:content.second"
                      components={{
                        p: <Text size={`lg`} />,
                        span: (
                          <button
                            style={{
                              color: 'hsl(354, 81%, 42%)',
                              fontWeight: 'bold',
                            }}
                          />
                        ),
                      }}
                    />
                  </Flex>
                  <Accordion
                    defaultIndex={expandedIndexIR || 99}
                    width="100%"
                    p={[1, 2, 4]}
                    mx={[1, 2, 4]}
                  >
                    <AccordionItem ref={IRref} bg={'gray.200'} width="100%">
                      <AccordionButton
                        fontStyle={'italic'}
                        color={`heading`}
                        borderRadius="10px"
                        borderColor="gray.200"
                        mt={2}
                        p={1}
                        bg={'gray.300'}
                      >
                        <Text size={`md`}>
                          {t(`offers:generic.howDoesItWork`)}
                        </Text>
                      </AccordionButton>
                      <AccordionPanel>
                        <List>
                          <Flex
                            align="flex-start"
                            justify="center"
                            direction="column"
                          >
                            <ListItem p={[1, 1, 2]}>
                              <Flex direction="column" align="flex-start">
                                <Flex direction="row">
                                  <FaCheckCircle />
                                  <Text size={`lg`} ml={[1, 2, 4]}>
                                    {t(`offers:content.accordionOne`)}
                                  </Text>
                                </Flex>
                                <Box>
                                  <Text size={`md`} fontStyle="italic">
                                    {t(`offers:content.accordionOneSub`)}
                                  </Text>
                                </Box>
                              </Flex>
                            </ListItem>
                            <ListItem p={[1, 1, 2]}>
                              <FaCheckCircle />
                              <Text size={`lg`} ml={[1, 2, 4]}>
                                {t(`offers:content.accordionTwo`)}
                              </Text>
                            </ListItem>
                            <ListItem p={[1, 1, 2]}>
                              <FaCheckCircle />
                              <Text size={`lg`} ml={[1, 2, 4]}>
                                {t(`offers:content.accordionThree`)}
                              </Text>
                            </ListItem>
                            <ListItem p={[1, 1, 2]}>
                              <FaCheckCircle />
                              <Text size={`lg`} ml={[1, 2, 4]}>
                                {t(`offers:content.accordionFour`)}
                              </Text>
                            </ListItem>
                          </Flex>
                        </List>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </Flex>
              </Box>
              <Box
                py={[4, 6, 8]}
                px={[6, null, 8]}
                bg="gray.100"
                borderRadius="0px 0px 10px 10px"
              >
                <Flex align="flex-start" justify="center" direction="column">
                  <Flex align="center">
                    <IconBox
                      size="md"
                      icon={<FaHouse color="white" />}
                      background={`heading`}
                      padding="10px"
                      mr={4}
                    />
                    <Trans
                      i18nKey="offers:content.third"
                      components={{
                        p: <Text size={`lg`} />,
                        span: (
                          <button
                            style={{
                              color: 'hsl(354, 81%, 42%)',
                              fontWeight: 'bold',
                            }}
                          />
                        ),
                      }}
                    />
                  </Flex>
                  <Accordion
                    defaultIndex={expandedIndexRFQ || 99}
                    width="100%"
                    p={[1, 2, 6]}
                    mx={[1, 2, 4]}
                  >
                    <AccordionItem ref={RFQref} bg="gray.100" width="100%">
                      <AccordionButton
                        width="100%"
                        fontStyle={'italic'}
                        mt={2}
                        p={1}
                        borderRadius="10px"
                        bg={'gray.300'}
                        marginLeft="0 auto"
                      >
                        <Text textAlign="left" size={`md`}>
                          {t(`offers:generic.howDoesItWork`)}
                        </Text>
                      </AccordionButton>
                      <AccordionPanel>
                        <List>
                          <Flex
                            align="flex-start"
                            justify="center"
                            direction="column"
                          >
                            <ListItem p={2}>
                              <Flex direction="column" align="center">
                                <Flex>
                                  <FaCheckCircle />
                                  <Text size={`lg`} ml={[1, 2, 4]}>
                                    {t(`offers:content.accordionFive`)}
                                  </Text>
                                </Flex>
                                <Box>
                                  <Text size={`md`} fontStyle="italic">
                                    {t(`offers:content.accordionFiveSub`)}
                                  </Text>
                                </Box>
                              </Flex>
                            </ListItem>
                            <ListItem p={2}>
                              <FaCheckCircle size={22} />
                              <Text size={`lg`} ml={[1, 2, 4]}>
                                {t(`offers:content.accordionSix`)}
                              </Text>
                            </ListItem>
                            <ListItem p={2}>
                              <FaCheckCircle />
                              <Text size={`lg`} ml={[1, 2, 4]}>
                                {t(`offers:content.accordionSeven`)}
                              </Text>
                            </ListItem>
                            <ListItem p={2}>
                              <FaCheckCircle />
                              <Text size={`lg`} ml={[1, 2, 4]}>
                                {t(`offers:content.accordionEight`)}
                              </Text>
                            </ListItem>
                          </Flex>
                        </List>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </Flex>
              </Box>
              <HomePreFooter />
            </Box>
          </Container>
        </Box>
      </Layout>
    </>
  );
};
export default Offers;
